import './ImageCarousel.scss';
import { PropertyDetailsResponseData } from '../../models/details.models';
import { useState } from 'react';
import { PropertySearchResTypeData } from '../../services/api';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';

import { theme } from '../../constants/theme/theme.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ImageCarouselBigModal } from './ImageCarouselBigModal';
import {
  HomeImageObject,
  HomeModelsResponseData,
} from '../../models/homes.models';
import {
  cloudinaryImage1080Config,
  cloudinaryImageThumbnailConfig,
  devOnlyCloudinaryInstance,
} from '../../utils/images.tsx';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

interface ImageCarouselProps {
  propertyData?: PropertyDetailsResponseData | undefined;
  propertySearchData?: PropertySearchResTypeData | undefined;
  homeModelsResponseData?: HomeModelsResponseData;
  showArrows?: boolean;
  mlsImage?: string;
  openBigModeOnClick?: boolean;
  fullHeight?: boolean;
  height?: string;
  smallArrows?: boolean;
  onClick?: () => void;
  highResolutionImages?: boolean;
}

export const ImageCarousel = ({
  propertyData,
  propertySearchData,
  homeModelsResponseData,
  onClick,
  showArrows = true,
  smallArrows = false,
  openBigModeOnClick = false,
  fullHeight = false,
  height,
  mlsImage = 'https://images.pexels.com/photos/4091975/pexels-photo-4091975.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  highResolutionImages = true,
}: ImageCarouselProps) => {
  const [bigModeOpen, setBigModeOpen] = useState(false);

  let images: HomeImageObject[] | string[];
  if (propertySearchData) {
    images = propertySearchData?.photosList?.length
      ? propertySearchData?.photosList
      : [mlsImage];
  } else if (homeModelsResponseData) {
    images = homeModelsResponseData.images.map((image) => {
      if (highResolutionImages) {
        return cloudinaryImage1080Config(
          devOnlyCloudinaryInstance,
          image.home_image_id
        );
      } else {
        return cloudinaryImageThumbnailConfig(
          devOnlyCloudinaryInstance,
          image.home_image_id
        );
      }
    });
  } else {
    images = [];
  }

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const maxHeight = isMobile ? '300px' : '550px';

  return (
    <div className="slider">
      <Swiper
        loop={true}
        pagination={{
          dynamicBullets: true,
          clickable: true,
        }}
        navigation={showArrows}
        height={300}
        modules={[Pagination, Navigation]}
        className="mySwiper"
        data-small-arrows={smallArrows}
      >
        {images.map((image, index) => (
          <div key={`div-${index}`} className="gallery">
            <SwiperSlide
              key={index}
              onClick={() => {
                if (openBigModeOnClick) {
                  setBigModeOpen(true);
                } else if (onClick) {
                  onClick();
                }
              }}
            >
              <LazyLoadImage
                key={`image-${index}`}
                alt="property"
                effect="blur"
                src={typeof image === 'string' ? image : image?.home_image_id}
                width="100%"
                height={height ? height : fullHeight ? maxHeight : '254px'}
                style={{ objectFit: 'cover' }}
                useIntersectionObserver={true}
              />
            </SwiperSlide>
          </div>
        ))}
      </Swiper>
      <ImageCarouselBigModal
        isOpen={bigModeOpen}
        setIsOpen={setBigModeOpen}
        propertyData={propertyData}
        propertySearchData={propertySearchData}
        homeModelsResponseData={homeModelsResponseData}
        mlsImage={mlsImage}
      />
    </div>
  );
};
